/**
 * <back-in-stock>: the popup from snippets/back-in-stock.liquid.
 *
 * Any element with data-action="open-back-in-stock" opens it for the variant in
 * its data-variant. Submitting subscribes the email to Klaviyo back in stock for
 * that variant, and to the newsletter list when the box is ticked.
 */
class BackInStock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("[data-bis-form]");
    this.errorBlock = this.querySelector("[data-bis-error]");
    this.emailInput = this.querySelector('[name="email"]');

    this.addEventListener("click", (event) => {
      if (event.target === this || event.target.closest('[data-action="close"]')) this.close();
    });

    this.form?.addEventListener("submit", (event) => {
      event.preventDefault();
      this.submit();
    });

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest('[data-action="open-back-in-stock"]');
      if (!trigger) return;

      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.classList.contains("open")) this.close();
    });
  }

  open(trigger) {
    this.variantId = trigger.dataset.variant;
    this.trigger = trigger;

    this.classList.remove("success", "loading");
    this.form?.reset();
    this.setError("");

    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";

    setTimeout(() => this.emailInput?.focus(), 100);
  }

  close() {
    if (!this.classList.contains("open")) return;

    this.classList.remove("open");
    document.documentElement.style.overflow = "";
    this.trigger?.focus();
  }

  setError(text) {
    if (this.errorBlock) this.errorBlock.textContent = text;
    this.emailInput?.toggleAttribute("aria-invalid", Boolean(text));
  }

  submit() {
    if (this.classList.contains("loading")) return;

    const email = this.emailInput.value.trim();
    if (!email || !this.emailInput.checkValidity()) {
      this.setError(this.dataset.errorEmail);
      return;
    }

    const companyId = this.dataset.companyId;
    if (!companyId || !this.variantId) {
      this.setError(this.dataset.error);
      return;
    }

    this.setError("");
    this.classList.add("loading");

    const requests = [
      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
        method: "POST",
        headers: { "content-type": "application/json", revision: "2024-02-15" },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: { data: { type: "profile", attributes: { email } } },
              channels: ["EMAIL"]
            },
            relationships: {
              variant: { data: { type: "catalog-variant", id: `$shopify:::$default:::${this.variantId}` } }
            }
          }
        })
      })
    ];

    const listId = this.dataset.listId;
    if (listId && this.querySelector('[name="consent"]')?.checked && typeof subscribeToNewsletter === "function") {
      requests.push(subscribeToNewsletter(companyId, listId, email));
    }

    Promise.all(requests)
      .then((responses) => {
        if (!responses.every((response) => response && response.ok)) throw new Error("Klaviyo request failed");
        this.classList.add("success");
      })
      .catch((error) => {
        console.error(error);
        this.setError(this.dataset.error);
      })
      .finally(() => this.classList.remove("loading"));
  }
}

if (!customElements.get("back-in-stock")) {
  customElements.define("back-in-stock", BackInStock);
}
